import React, { useState } from 'react';
import api from '../../lib/api';
import { Lightbulb, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';
import { apiErrorMessage } from '../../lib/utils';

export default function SuggestItemModal({ open, onClose, itemType, onSuggestionCreated }) {
  const [itemName, setItemName] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const getTypeLabel = () => {
    const labels = {
      'medication': 'Medication',
      'lab_test': 'Lab Test',
      'imaging_test': 'Imaging Test',
      'procedure': 'Procedure'
    };
    return labels[itemType] || 'Item';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!itemName.trim()) {
      toast.error('Item name is required');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await api.post('/suggest-master-item', {
        item_type: itemType,
        item_name: itemName.trim(),
        item_details: {
          description: description.trim()
        }
      });
      
      toast.success(response.data.message || 'Suggestion submitted successfully!');
      
      // Reset form
      setItemName('');
      setDescription('');
      onClose();
      
      // Callback to parent if needed
      if (onSuggestionCreated) {
        onSuggestionCreated(response.data);
      }
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to submit suggestion'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setItemName('');
      setDescription('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-amber-500" />
            Suggest New {getTypeLabel()}
          </DialogTitle>
          <DialogDescription>
            Your suggestion will be reviewed by the platform administrator and added to the global master data if approved.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div>
            <Label htmlFor="item-name">
              {getTypeLabel()} Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="item-name"
              value={itemName}
              onChange={(e) => setItemName(e.target.value)}
              placeholder={`Enter ${getTypeLabel().toLowerCase()} name`}
              required
              autoFocus
              disabled={submitting}
            />
          </div>
          
          <div>
            <Label htmlFor="description">Description (Optional)</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add any additional details..."
              rows={3}
              disabled={submitting}
            />
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              💡 Your suggestion will be reviewed by the platform administrator. You can track the status of your suggestions in your profile.
            </p>
          </div>
        </form>
        
        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !itemName.trim()}
            className="bg-amber-500 hover:bg-amber-600"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Lightbulb className="w-4 h-4 mr-2" />
                Submit Suggestion
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
